/**
 * @file ScheduleBoardroomDialog.tsx
 * @brief "Schedule for later" — web parity for mobile ScheduleMeetingModal.
 *
 * @author DES
 * @date 28-07-2026
 * @updated 29-07-2026 — FormSheet + GridBlock chrome
 */

import { useEffect, useRef, useState } from "react";
import { LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { DateTimePicker } from "@/components/ui/DateTimePicker";
import {
  FormFieldGroup,
  FormSheet,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { Input } from "@/components/ui/Input";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { ToastAction } from "@/components/ui/Toast";
import { cn } from "@/shared/theme/cn";
import type { BoardroomDraftParticipant } from "./capture/boardroomDraftStore";
import { createScheduleAction, SCHEDULE_ERROR_HINT, SCHEDULE_ERROR_TITLE } from "./scheduleFlow";
import { scheduleAgenda } from "./scheduledSide";
import {
  createScheduledMeeting,
  updateScheduledMeeting,
  type ScheduledMeeting,
} from "./scheduledMeetingsApi";

function nextRoundHourIso(): string {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d.toISOString();
}

function toApiParticipants(people: BoardroomDraftParticipant[]) {
  return people.map((p) =>
    p.isExternal
      ? {
          email: p.email,
          name: p.name,
          isExternal: true,
          shared: p.shared,
        }
      : {
          userId: p.id,
          name: p.name,
          isExternal: false,
          shared: p.shared,
        },
  );
}

export type ScheduleBoardroomDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  targetLanguages: string[];
  participants: BoardroomDraftParticipant[];
  /** Agenda notes picked on setup; saved with the meeting (none for Notes). */
  prepNoteIds?: string[];
  /** Edit / reschedule mode */
  existingMeeting?: ScheduledMeeting | null;
  onScheduled: (meeting: ScheduledMeeting) => void;
  /**
   * New meeting (DES 10-10-2026, scheduleFlow.ts, same as the phone): after a
   * successful save the host resets its form and REPLACES itself with
   * Scheduled Meetings, the new meeting highlighted. Called instead of
   * `onScheduled`; the confirmation toast is shown here.
   */
  onCreated?: (params: { highlightId: string; message: string }) => void;
  /** New meeting: clear the host form (title, people, languages, agenda, tier). */
  onResetForm?: () => void;
};

export function ScheduleBoardroomDialog({
  open,
  onOpenChange,
  title,
  targetLanguages,
  participants,
  prepNoteIds,
  existingMeeting,
  onScheduled,
  onCreated,
  onResetForm,
}: ScheduleBoardroomDialogProps) {
  const isEdit = Boolean(existingMeeting);
  const [when, setWhen] = useState<string | undefined>(nextRoundHourIso());
  const [saving, setSaving] = useState(false);

  // New meeting: one save at a time; on failure the sheet stays open with
  // everything kept and an error with Retry (scheduleFlow.ts). Latest props
  // through a ref so the action, and its in-flight guard, is made once.
  const latest = useRef({
    title,
    targetLanguages,
    participants,
    prepNoteIds,
    when: undefined as string | undefined,
    onCreated,
    onResetForm,
    onOpenChange,
  });
  const createAction = useRef(
    createScheduleAction<ScheduledMeeting>({
      save: () => {
        const l = latest.current;
        return createScheduledMeeting({
          title: l.title.trim(),
          targetLanguages: l.targetLanguages,
          scheduledAt: l.when!,
          participants: toApiParticipants(l.participants),
          ...scheduleAgenda(l.prepNoteIds, false),
        });
      },
      resetForm: () => latest.current.onResetForm?.(),
      showScheduled: (params) => {
        latest.current.onOpenChange(false);
        toast({ variant: "success", title: params.message });
        latest.current.onCreated?.(params);
      },
      showError: (err, retry) => {
        toast({
          variant: "destructive",
          title: SCHEDULE_ERROR_TITLE,
          description: `${err instanceof Error ? err.message : "Please try again."} ${SCHEDULE_ERROR_HINT}`,
          action: (
            <ToastAction altText="Retry" onClick={retry}>
              Retry
            </ToastAction>
          ),
        });
      },
      setBusy: setSaving,
    }),
  ).current;

  useEffect(() => {
    if (!open) return;
    if (existingMeeting) {
      const t = new Date(existingMeeting.scheduled_at);
      setWhen(
        Number.isNaN(t.getTime()) || t.getTime() < Date.now()
          ? nextRoundHourIso()
          : t.toISOString(),
      );
    } else {
      setWhen(nextRoundHourIso());
    }
  }, [open, existingMeeting]);

  const onSave = async () => {
    if (saving) return;
    if (!when) {
      toast({
        variant: "destructive",
        title: "Pick a time",
        description: "Choose when this boardroom should start.",
      });
      return;
    }
    if (new Date(when).getTime() < Date.now()) {
      toast({
        variant: "destructive",
        title: "Invalid time",
        description: "Please pick a time in the future.",
      });
      return;
    }
    const trimmed = title.trim();
    if (!trimmed && !isEdit) {
      toast({
        variant: "destructive",
        title: "Title required",
        description: "Enter a meeting title before scheduling.",
      });
      return;
    }

    if (!existingMeeting) {
      latest.current = {
        ...latest.current,
        title,
        targetLanguages,
        participants,
        prepNoteIds,
        when,
        onCreated,
        onResetForm,
        onOpenChange,
      };
      await createAction();
      return;
    }

    setSaving(true);
    try {
      const meeting = await updateScheduledMeeting(existingMeeting.id, {
        scheduledAt: when,
      });
      onScheduled(meeting);
      onOpenChange(false);
      toast({
        variant: "success",
        title: "Meeting rescheduled",
        description: "Invitees will get an updated calendar invite.",
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Could not schedule",
        description:
          err instanceof ApiClientError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const meetingTitle = title.trim() || existingMeeting?.title || "Untitled boardroom";

  return (
    <FormSheet open={open} onOpenChange={onOpenChange}>
      {/* Cmd+K-style palette frame; one title and one date is a short form,
          so max-w-3xl keeps it from looking empty (DES 01-10-2026: form
          standard; was a 28rem right sheet). */}
      <FormSheetContent side="palette" className="max-w-3xl">
        <FormSheetFrame>
          <FormSheetHeader>
            <FormSheetTitle>{isEdit ? "Edit meeting time" : "Schedule for later"}</FormSheetTitle>
            <FormSheetDescription>
              {isEdit
                ? "Move the start time. Title, languages, and invitees stay the same."
                : "Optional. You can still start a boardroom immediately anytime."}
            </FormSheetDescription>
          </FormSheetHeader>

          <FormSheetBody>
            <FormSheetSection
              title="When"
              subtitle={
                isEdit
                  ? undefined
                  : `${participants.length} invitee${participants.length === 1 ? "" : "s"}${
                      targetLanguages.length > 0
                        ? ` · ${targetLanguages.length} language${targetLanguages.length === 1 ? "" : "s"}`
                        : ""
                    }`
              }
            >
              {/* Meeting and start time on one row, both bare fields so the
                  hairlines line up (DES 01-10-2026: form standard). The
                  title is read-only here: it is set on the boardroom form. */}
              <FormSheetFields>
                <FormSheetField>
                  <FormFieldGroup label="Meeting" htmlFor="schedule-boardroom-title">
                    <Input
                      capitalizeFirst
                      id="schedule-boardroom-title"
                      value={meetingTitle}
                      readOnly
                      tabIndex={-1}
                      className="cursor-default font-semibold"
                    />
                  </FormFieldGroup>
                </FormSheetField>
                <FormSheetField>
                  <FormFieldGroup label="Starts at" required>
                    <DateTimePicker
                      value={when}
                      onChange={setWhen}
                      title="Boardroom start"
                      disablePast
                      description="Pick a future date and time."
                    />
                  </FormFieldGroup>
                </FormSheetField>
              </FormSheetFields>
            </FormSheetSection>
          </FormSheetBody>

          <FormSheetFooter>
            <FormSheetFooterCell>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={saving}
                className={formSheetActionClass}
              >
                Cancel
              </Button>
            </FormSheetFooterCell>
            <FormSheetFooterCell>
              <Button
                type="button"
                onClick={() => void onSave()}
                disabled={saving}
                className={cn(
                  formSheetActionClass,
                  "bg-primary text-primary-foreground hover:bg-primary/90",
                )}
              >
                {saving ? (
                  <>
                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                    Saving…
                  </>
                ) : isEdit ? (
                  "Save time"
                ) : (
                  "Schedule"
                )}
              </Button>
            </FormSheetFooterCell>
          </FormSheetFooter>
        </FormSheetFrame>
      </FormSheetContent>
    </FormSheet>
  );
}
